import { Suspense, lazy } from 'react';
import { Navigate } from 'react-router-dom';
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';

import useSentryRoutes from '../sentry-config';
import LoadingScreen from '../components/LoadingScreen';
import { PATH_DASHBOARD } from './paths';

const Loadable = (Component) => (props) =>
  (
    <Suspense fallback={<LoadingScreen isDashboard={window.location.pathname.includes('/dashboard')} />}>
      <Component {...props} />
    </Suspense>
  );

const Login = Loadable(lazy(() => import('../pages/auth/Login')));
const ResetPassword = Loadable(lazy(() => import('../pages/auth/ResetPassword')));
const ForgotPassword = Loadable(lazy(() => import('../pages/auth/ForgotPassword')));
const Home = Loadable(lazy(() => import('../pages/dashboard/home/Home')));
const DashboardPage = Loadable(lazy(() => import('../pages/dashboard/DashboardPage')));
const DashboardPowerBi = Loadable(lazy(() => import('../pages/dashboard/DashboardPowerBi')));
const DashboardStore = Loadable(lazy(() => import('../pages/dashboard/DashboardStore')));
const ClientList = Loadable(lazy(() => import('../pages/dashboard/clients/ClientList')));
const ClientWrapper = Loadable(lazy(() => import('../pages/dashboard/clients/ClientWrapper')));
const Expireds = Loadable(lazy(() => import('../pages/dashboard/Clientes/components/CreditsTab')));
Loadable(lazy(() => import('../pages/dashboard/Clientes/components/ExpiryTab')));
const CreditProfile = Loadable(lazy(() => import('../pages/dashboard/clients/details/creditProfile/CreditProfile')));
const CreditProfileDetail = Loadable(
  lazy(() => import('../pages/dashboard/clients/details/creditProfile/CreditProfileDetail'))
);

const CreditDetails = Loadable(lazy(() => import('../pages/dashboard/clients/details/credits/CreditDetails')));
const ClientDetail = Loadable(lazy(() => import('../pages/dashboard/clients/details/ClientDetail')));
const CoreLifeCycleTab = Loadable(lazy(() => import('../pages/dashboard/clients/details/CoreLifeCycleTab')));
const GeneralSection = Loadable(lazy(() => import('../pages/dashboard/clients/details/general/GeneralSection')));
const DocsSection = Loadable(lazy(() => import('../pages/dashboard/clients/details/docs/DocsSection')));
const ApplicationsList = Loadable(lazy(() => import('../pages/dashboard/applications/ApplicationsList')));
const ApplicationDetail = Loadable(lazy(() => import('../pages/dashboard/applications/ApplicationDetail')));
const PersonalLoansMonitor = Loadable(lazy(() => import('../pages/dashboard/personalLoans/PersonalLoansMonitor')));
const EmployeePayrollMonitor = Loadable(
  lazy(() => import('../pages/dashboard/employeePayroll/EmployeePayrollMonitor'))
);
const PersonalLoansReconciliation = Loadable(
  lazy(() => import('../pages/dashboard/personalLoans/PersonalLoansReconciliation'))
);
const StoresList = Loadable(lazy(() => import('../pages/dashboard/stores/storesList/StoresList')));
Loadable(lazy(() => import('../pages/dashboard/debtCollection/CollectionDebtList')));
Loadable(lazy(() => import('../pages/dashboard/debtCollection/CollectionHistoryList')));
Loadable(lazy(() => import('../pages/dashboard/debtCollection/CollectionDetail')));
const TicketList = Loadable(lazy(() => import('../pages/dashboard/tickets/TicketList')));
const TicketDetail = Loadable(lazy(() => import('../pages/dashboard/tickets/details/TicketDetail')));
const ApplicationsReports = Loadable(lazy(() => import('../pages/dashboard/reports/ApplicationsReports')));
const GoToCreditReports = Loadable(lazy(() => import('../pages/dashboard/reports/GoToCreditReports')));
const MassForgivenessIndividual = Loadable(lazy(() => import('../pages/dashboard/reports/MassForgivenessIndividual')));
const MassForgivenessReport = Loadable(lazy(() => import('../pages/dashboard/reports/MassForgivenessReport')));
const WebappUsers = Loadable(lazy(() => import('../pages/dashboard/reports/WebappUsers')));
const WalletpunishmentReport = Loadable(lazy(() => import('../pages/dashboard/reports/WalletpunishmentReport')));
const HistoricalCampaignIndividual = Loadable(
  lazy(() => import('../pages/dashboard/reports/HistoricalCampaignIndividual'))
);
const HistoricalCampaignReport = Loadable(lazy(() => import('../pages/dashboard/reports/HistoricalCampaignReport')));
const GoToCastigosReports = Loadable(lazy(() => import('../pages/dashboard/reports/GoToCastigosReports')));
const CreditConsultReports = Loadable(lazy(() => import('../pages/dashboard/reports/CreditConsultReports')));
const NipsList = Loadable(lazy(() => import('../pages/dashboard/nips/NipsList')));
const NipsListAuth = Loadable(lazy(() => import('../pages/dashboard/nips/NipsListAuth')));
const HistoryScorePage = Loadable(lazy(() => import('../pages/dashboard/historyScorePage/HistoryScorePage')));
const ConversationsList = Loadable(lazy(() => import('../pages/dashboard/conversations/conversationsList')));
const ConversationItem = Loadable(lazy(() => import('../pages/dashboard/conversations/conversationItem')));
Loadable(lazy(() => import('../pages/dashboard/creditCollection/AssignedPortfolio')));
const AssignedPortfolioMirror = Loadable(
  lazy(() => import('../pages/dashboard/creditCollection/AssignedPortfolioMirror'))
);
const AssignedPortfolioGestor = Loadable(
  lazy(() => import('../pages/dashboard/creditCollection/AssignedPortfolioGestor'))
);

const SegmentPortfolio = Loadable(lazy(() => import('../pages/dashboard/creditCollection/SegmentPortfolio')));
Loadable(lazy(() => import('../pages/dashboard/creditCollection/SubSegmentPortfolio')));
const MassContact = Loadable(lazy(() => import('../pages/dashboard/creditCollection/MassContact')));
const MovementsTable = Loadable(lazy(() => import('../pages/dashboard/creditCollection/MovementsTable')));
const TicketsTable = Loadable(lazy(() => import('../pages/dashboard/creditCollection/TicketsTable')));
const LiquidacionAnticipada = Loadable(lazy(() => import('../pages/dashboard/creditCollection/LiquidacionAnticipada')));
const TicketCompra = Loadable(lazy(() => import('../pages/dashboard/creditCollection/TicketCompra')));
const ChatsTable = Loadable(lazy(() => import('../pages/dashboard/creditCollection/ChatsTable')));
const ModalConversationActions = Loadable(
  lazy(() => import('../pages/dashboard/tickets/details/chat/ModalConversationActions'))
);
Loadable(lazy(() => import('../pages/dashboard/creditCollection/ForgivenessInterest')));
Loadable(lazy(() => import('../pages/dashboard/creditCollection/Conversation')));
const SmsSettings = Loadable(lazy(() => import('../pages/dashboard/settings/SmsSettings/SmsSettings')));
const Campaigns = Loadable(lazy(() => import('../pages/dashboard/settings/Campaigns/Campaigns')));
const BalanceReport = Loadable(lazy(() => import('../pages/dashboard/settings/Reports/BalanceReport/BalanceReport')));
const Productivity = Loadable(lazy(() => import('../pages/dashboard/settings/Reports/Productivity/Productivity')));
const Application = Loadable(lazy(() => import('../pages/dashboard/settings/BadDebt/Application')));
const Condonation = Loadable(lazy(() => import('../pages/dashboard/settings/BadDebt/Condonation')));
Loadable(lazy(() => import('../pages/dashboard/settings/BadDebt/Parameterization')));
const Assignation = Loadable(lazy(() => import('../pages/dashboard/settings/AssignedPortfolio/Assignation')));
const Despacho = Loadable(lazy(() => import('../pages/dashboard/settings/AssignedPortfolio/Despacho')));
Loadable(lazy(() => import('../pages/dashboard/settings/AssignedPortfolio/PortfolioView')));
Loadable(lazy(() => import('../pages/dashboard/settings/UserMaintenance/UserMaintenance')));
const ConfigPayPage = Loadable(lazy(() => import('../pages/dashboard/configuracionpagos/ConfigPayPage')));
const DispersionPage = Loadable(lazy(() => import('../pages/dashboard/configuracionpagos/DispersionPage')));
const PaymentAccessPage = Loadable(lazy(() => import('../pages/dashboard/configuracionpagos/PaymentAccessPage')));
Loadable(lazy(() => import('../pages/dashboard/Clientes/Clientes')));
Loadable(lazy(() => import('../pages/dashboard/Clientes/PerfilCliente')));
const LiquidacionReglasPage = Loadable(lazy(() => import('../pages/dashboard/creditCollection/LiquidacionReglasPage')));
// tables pages
Loadable(lazy(() => import('../pages/dashboard/acreditados/LiquidacionPage')));
const CondonacionPage = Loadable(lazy(() => import('../pages/dashboard/acreditados/CondonacionPage')));
const CastigoPage = Loadable(lazy(() => import('../pages/dashboard/acreditados/CastigoPage')));
// products
Loadable(lazy(() => import('../pages/dashboard/FinancialProducts/FinancialProductsPage')));
const CreateProduct = Loadable(lazy(() => import('../pages/dashboard/FinancialProducts/CreateProduct')));
const ProductList = Loadable(lazy(() => import('../pages/dashboard/FinancialProducts/ProductList')));
Loadable(lazy(() => import('../pages/dashboard/FinancialProducts/ProductsPage')));
const EditProduct = Loadable(lazy(() => import('../pages/dashboard/FinancialProducts/EditProduct')));
const ProductDetail = Loadable(lazy(() => import('../pages/dashboard/FinancialProducts/ProductDetail')));
const LegalarioLifeproofConfig = Loadable(
  lazy(() => import('../pages/dashboard/FinancialProducts/LegalarioLifeproofConfig'))
);
const CreditDecisionEngineConfig = Loadable(
  lazy(() => import('../pages/dashboard/FinancialProducts/CreditDecisionEngineConfig'))
);
const ModalPassword = Loadable(lazy(() => import('../pages/dashboard/FinancialProducts/ModalPassword')));
const ModalSupportDocuments = Loadable(
  lazy(() => import('../pages/dashboard/FinancialProducts/ModalSupportDocuments'))
);
const ModalCreditCircle = Loadable(lazy(() => import('../pages/dashboard/FinancialProducts/ModalCreditCircle')));
const ModalReenviarLiga = Loadable(lazy(() => import('../pages/dashboard/FinancialProducts/ModalReenviarLiga')));
const ModalRegenerarLiga = Loadable(lazy(() => import('../pages/dashboard/FinancialProducts/ModalRegenerarLiga')));
const ModalUpdateClientWatcher = Loadable(
  lazy(() => import('../pages/dashboard/FinancialProducts/ModalUpdateClientWatcher'))
);

// monitor
const WatcherReport = Loadable(lazy(() => import('../pages/dashboard/monitor/WatcherReport')));

// employees
const EmpleadosPage = Loadable(lazy(() => import('../pages/dashboard/empleados/EmpleadosPage')));
const MenusPages = Loadable(lazy(() => import('../pages/dashboard/empleados/MenusPages')));
//
const ComingSoon = Loadable(lazy(() => import('../pages/ComingSoon')));
const Maintenance = Loadable(lazy(() => import('../pages/Maintenance')));
const Page500 = Loadable(lazy(() => import('../pages/Page500')));
const Page403 = Loadable(lazy(() => import('../pages/Page403')));
const Page404 = Loadable(lazy(() => import('../pages/Page404')));

// Antifraude
const RulesPage = Loadable(lazy(() => import('../pages/dashboard/antifraude/RulesPage')));
const BlacklistPage = Loadable(lazy(() => import('../pages/dashboard/antifraude/BlacklistPage')));
const RulesAuditPage = Loadable(lazy(() => import('../pages/dashboard/antifraude/RulesAuditPage')));

// Tienda
const DocumentosPendientes = Loadable(lazy(() => import('../pages/dashboard/Tienda/DocumentosPendientes')));
const DocumentosCliente = Loadable(lazy(() => import('../pages/dashboard/clients/details/docs/DocumentosCliente')));
const LegalarioLifeproofClient = Loadable(
  lazy(() => import('../pages/dashboard/clients/details/lifeproof/LegalarioLifeproofClient'))
);

// Reportes Contactacion
const ClientesPromesasPagoReport = Loadable(
  lazy(() => import('../pages/dashboard/reports/ClientesPromesasPagoReport'))
);
const TrazabilidadClientesReport = Loadable(
  lazy(() => import('../pages/dashboard/reports/TrazabilidadClientesReport'))
);
const AsignacionesGestorReport = Loadable(lazy(() => import('../pages/dashboard/reports/AsignacionesGestorReport')));
const MensajesHistoricosReport = Loadable(lazy(() => import('../pages/dashboard/reports/MensajesHistoricosReport')));

// Reportes Configuracion
const SegmentacionesSGCReport = Loadable(lazy(() => import('../pages/dashboard/reports/SegmentacionesSGCReport')));
const SubsegmentacionesSGCReport = Loadable(
  lazy(() => import('../pages/dashboard/reports/SubsegmentacionesSGCReport'))
);
const ConfigSmsEmailsReport = Loadable(lazy(() => import('../pages/dashboard/reports/ConfigSmsEmailsReport')));

// Reportes Campañas
const CampanasSGCReport = Loadable(lazy(() => import('../pages/dashboard/reports/CampanasSGCReport')));
const CondonacionesMasivasReport = Loadable(
  lazy(() => import('../pages/dashboard/reports/CondonacionesMasivasReport'))
);
const MensajesClientesReport = Loadable(lazy(() => import('../pages/dashboard/reports/MensajesClientesReport')));

// Reportes Gestores
const MontosRecuperadosReport = Loadable(lazy(() => import('../pages/dashboard/reports/MontosRecuperadosReport')));
const ProductividadGestoresReport = Loadable(
  lazy(() => import('../pages/dashboard/reports/ProductividadGestoresReport'))
);

// Reportes Castigo
const ClientesPrescastigoCastigoReport = Loadable(
  lazy(() => import('../pages/dashboard/reports/ClientesPrescastigoCastigoReport'))
);
const EventosCastigoCarteraReport = Loadable(
  lazy(() => import('../pages/dashboard/reports/EventosCastigoCarteraReport'))
);

// Core Life Cycle
const Categories = Loadable(lazy(() => import('../pages/dashboard/clc/Category/Categories')));
const RulesAdministration = Loadable(lazy(() => import('../pages/dashboard/clc/Rules/RulesAdministration')));
const StoresAdministration = Loadable(lazy(() => import('../pages/dashboard/clc/StoresAdministration')));
const OperationalNotifications = Loadable(
  lazy(() => import('../pages/dashboard/clc/Notifications/OperationalNotifications'))
);
const ClientAssignments = Loadable(lazy(() => import('../pages/dashboard/clc/Assignments/ClientAssignments')));

// reasignacion button
const Reasign = Loadable(lazy(() => import('../pages/dashboard/reasign/Reasign')));

// modal line increase
const ModalLineIncrease = Loadable(lazy(() => import('../pages/dashboard/FinancialProducts/ModalLineIncrease')));

const ModalPowerBI = Loadable(lazy(() => import('../pages/dashboard/FinancialProducts/ModalPowerBI')));

export default function Router() {
  return useSentryRoutes([
    // ---------------- AUTH ----------------
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        { path: 'forgot-password', element: <ForgotPassword /> },
        { path: 'reset-password/:token', element: <ResetPassword /> },
      ],
    },
    {
      path: 'historial-crediticio/:id',
      element: (
        <AuthGuard>
          <HistoryScorePage />
        </AuthGuard>
      ),
    },

    // ---------------- DASHBOARD ----------------
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { index: true, element: <Navigate to={PATH_DASHBOARD.controlTable.approvals} replace /> },
        { path: 'home', element: <Home /> },
        { path: 'dashboard', element: <DashboardPage /> },
        { path: 'Dirección', element: <DashboardPowerBi /> },
        { path: 'Tienda', element: <DashboardStore /> },
        {
          path: 'mesa-control',
          children: [
            { path: 'aprobaciones', element: <TicketList /> },
            { path: 'aprobaciones/:id', element: <TicketDetail /> },
            { path: 'conversaciones', element: <ConversationsList /> },
            { path: 'conversaciones/actualizar-estado', element: <ModalConversationActions /> },
            { path: 'conversaciones/:id', element: <ConversationItem /> },
            { path: 'solicitudes', element: <ApplicationsList /> },
            { path: 'solicitudes/:id', element: <ApplicationDetail /> },
            { path: 'prestamos-personales', element: <PersonalLoansMonitor /> },
            { path: 'nips-originacion', element: <NipsList /> },
            { path: 'nips-autenticacion', element: <NipsListAuth /> },
            { path: 'documentos-pendientes', element: <DocumentosPendientes /> },
          ],
        },
        {
          path: 'creditos-empleados',
          children: [{ path: 'nomina', element: <EmployeePayrollMonitor /> }],
        },
        {
          path: 'clientes',
          children: [
            {
              path: 'cartera-asignada',
              children: [
                { index: true, element: <AssignedPortfolioGestor /> },
                {
                  path: 'documentos',
                  children: [
                    { index: true, element: <Page404 /> },
                    { path: ':id', element: <MovementsTable /> },
                  ],
                },
              ],
            },
            {
              path: 'clientes',
              children: [
                { index: true, element: <ClientList /> },
                { path: 'perfil-de-credito/:profileId', element: <CreditProfile /> },
                { path: 'perfil-de-credito-detalle/:profileId', element: <CreditProfileDetail /> },
                { path: 'linea-de-credito/:ceditId', element: <CreditDetails /> },
                {
                  path: 'amortizacion',
                  children: [
                    { index: true, element: <Page404 /> },
                    { path: ':id', element: <TicketsTable /> },
                  ],
                },
                {
                  path: ':id',
                  element: <ClientWrapper />,
                  children: [
                    {
                      path: '',
                      element: <ClientDetail />,
                      children: [
                        { index: true, element: <GeneralSection /> },
                        { path: 'core-life-cycle', element: <CoreLifeCycleTab /> },
                        { path: 'creditos', element: <Expireds /> },
                        { path: 'movimientos/:proscai_id', element: <MovementsTable /> },
                        { path: 'vencimientos/:proscai_id', element: <TicketsTable /> },
                        { path: 'documentos-pendientes/:curp', element: <DocumentosPendientes /> },
                        { path: 'documentos-firmados/:curp', element: <DocumentosCliente /> },
                        { path: 'pruebas-vida/:curp', element: <LegalarioLifeproofClient /> },
                        { path: 'documentos', element: <DocsSection /> },
                        { path: 'interacciones/:proscai_id', element: <ChatsTable /> },
                        { path: 'liquidacion-anticipada/:proscai_id', element: <LiquidacionAnticipada /> },
                        { path: 'ticket-compra/:proscai_id', element: <TicketCompra /> },
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          path: 'campanas',
          children: [
            { path: 'sms', element: <SmsSettings /> },
            { path: 'programacion', element: <Campaigns /> },
            { path: 'contacto-masivo', element: <MassContact /> },
            { path: 'condonacion-masiva', element: <CondonacionPage /> },
          ],
        },
        {
          path: 'cartera',
          children: [
            { path: 'asignar-gestores', element: <Assignation /> },
            { path: 'asignar-despachos', element: <Despacho /> },
            { path: 'reasignacion-segmentos', element: <AssignedPortfolioMirror /> },
          ],
        },
        {
          path: 'clc',
          children: [
            { path: 'categorias', element: <Categories /> },
            { path: 'administracion-de-reglas', element: <RulesAdministration /> },
            { path: 'administracion-de-tiendas', element: <StoresAdministration /> },
            { path: 'notificaciones-operativas', element: <OperationalNotifications /> },
            { path: 'asignaciones', element: <ClientAssignments /> },
          ],
        },
        {
          path: 'configuracion',
          children: [
            {
              path: 'seguridad',
              children: [
                { path: 'roles-permisos', element: <MenusPages /> },
                { path: 'usuarios', element: <EmpleadosPage /> },
              ],
            },
            {
              path: 'reglas',
              children: [
                { path: 'liquidacion-anticipada', element: <LiquidacionReglasPage /> },
                { path: 'pre-castigo', element: <Application /> },
                { path: 'condonacion', element: <Condonation /> },
              ],
            },
            {
              path: 'mantenimiento',
              children: [
                { path: 'cartera', element: <SegmentPortfolio /> },
                { path: 'reasignacion', element: <Reasign /> },
                {
                  path: 'castigo',
                  children: [
                    { index: true, element: <CastigoPage /> },
                    {
                      path: 'documentos',
                      children: [
                        { index: true, element: <Page404 /> },
                        { path: ':id', element: <MovementsTable /> },
                      ],
                    },
                  ],
                },
                {
                  path: 'power-bi',
                  element: <ModalPowerBI />,
                },
              ],
            },
            {
              path: 'productos-financieros',
              children: [
                { index: true, element: <Navigate to="alta" replace /> },
                { path: 'alta', element: <CreateProduct /> },
                { path: 'alta/:productId', element: <CreateProduct /> },
                { path: 'gestion', element: <ProductList /> },
                { path: 'prueba-vida', element: <LegalarioLifeproofConfig /> },
                { path: 'motor-de-decision', element: <CreditDecisionEngineConfig /> },
                { path: 'editproduct/:uuid', element: <EditProduct /> },
                { path: 'product/:uuid/view', element: <ProductDetail /> },
              ],
            },
            {
              path: 'soporte',
              children: [
                { path: 'roles-permisos', element: <MenusPages /> },
                { path: 'usuarios', element: <EmpleadosPage /> },
                { path: 'password', element: <ModalPassword /> },
                { path: 'documentos', element: <ModalSupportDocuments /> },
                { path: 'circulo-credito', element: <ModalCreditCircle /> },
                { path: 'reenviar-liga', element: <ModalReenviarLiga /> },
                { path: 'regenerar-liga', element: <ModalRegenerarLiga /> },
                { path: 'incremento-linea', element: <ModalLineIncrease /> },
                { path: 'actualiza-saldo-cliente', element: <ModalUpdateClientWatcher /> },
              ],
            },
            {
              path: 'pagos',
              children: [
                { path: 'configuracion', element: <ConfigPayPage /> },
                { path: 'dispersion', element: <DispersionPage /> },
                { path: 'habilitados', element: <PaymentAccessPage /> },
              ],
            },
          ],
        },
        {
          path: 'watcher-report',
          element: <WatcherReport />,
        },
        {
          path: 'reportes',
          children: [
            { path: 'monitor-pagos', element: <PersonalLoansReconciliation /> },
            {
              path: 'contacto',
              children: [
                { index: true, element: <HistoricalCampaignReport /> },
                { path: 'individual', element: <HistoricalCampaignIndividual /> },
                { path: 'masivo', element: <HistoricalCampaignReport /> },
              ],
            },
            {
              path: 'castigo',
              children: [
                { path: 'eventos', element: <GoToCastigosReports /> },
                { path: 'detalle', element: <WalletpunishmentReport /> },
              ],
            },
            {
              path: 'condonacion',
              children: [
                { path: 'individual', element: <MassForgivenessIndividual /> },
                { path: 'masiva', element: <MassForgivenessReport /> },
              ],
            },
            {
              path: 'originacion',
              children: [
                { path: 'reporte-solicitudes', element: <ApplicationsReports /> },
                { path: 'pases-credito', element: <GoToCreditReports /> },
                { path: 'circulo-credito', element: <CreditConsultReports /> },
                { path: 'usuarios-webapp', element: <WebappUsers /> },
              ],
            },
            {
              path: 'configuracion',
              children: [
                { path: 'segmentaciones-sgc', element: <SegmentacionesSGCReport /> },
                { path: 'subsegmentaciones-sgc', element: <SubsegmentacionesSGCReport /> },
                { path: 'config-sms-emails', element: <ConfigSmsEmailsReport /> },
              ],
            },
            {
              path: 'contactacion',
              children: [
                { path: 'clientes-promesas-pago', element: <ClientesPromesasPagoReport /> },
                { path: 'trazabilidad-clientes', element: <TrazabilidadClientesReport /> },
                { path: 'asiganaciones-gestores', element: <AsignacionesGestorReport /> },
                { path: 'mensajes-historicos', element: <MensajesHistoricosReport /> },
              ],
            },
            {
              path: 'campanas',
              children: [
                { path: 'campanas-sgc', element: <CampanasSGCReport /> },
                { path: 'condonaciones-masivas', element: <CondonacionesMasivasReport /> },
                { path: 'mensajes-clientes', element: <MensajesClientesReport /> },
              ],
            },
            {
              path: 'gestores',
              children: [
                { path: 'montos-recuperados', element: <MontosRecuperadosReport /> },
                { path: 'productividad-gestores', element: <ProductividadGestoresReport /> },
              ],
            },
            {
              path: 'castigo-reportes',
              children: [
                { path: 'clientes-prescastigo-castigo', element: <ClientesPrescastigoCastigoReport /> },
                { path: 'eventos-castigo-cartera', element: <EventosCastigoCarteraReport /> },
              ],
            },
            {
              path: 'saldos',
              children: [
                { path: 'reporte-saldos', element: <BalanceReport /> },
                { path: 'productividad-gestores', element: <Productivity /> },
              ],
            },
          ],
        },

        // === 🏬 Tiendas ===
        {
          path: 'tiendas',
          element: <StoresList />,
        },
        // === 🛡️ Antifraude ===
        {
          path: 'antifraude',
          children: [
            { path: 'reglas', element: <RulesPage /> },
            { path: 'blacklist', element: <BlacklistPage /> },
            { path: 'auditoria-reglas', element: <RulesAuditPage /> },
          ],
        },
      ],
    },

    // ---------------- PÁGINAS DE ERROR ----------------
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: 'coming-soon', element: <ComingSoon /> },
        { path: 'maintenance', element: <Maintenance /> },
        { path: '500', element: <Page500 /> },
        { path: '404', element: <Page404 /> },
        { path: '403', element: <Page403 /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },

    // ---------------- REDIRECCIÓN RAÍZ ----------------
    {
      path: '/',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [{ index: true, element: <Navigate to={PATH_DASHBOARD.controlTable.approvals} /> }],
    },
  ]);
}
